import * as pdfjs from 'pdfjs-dist'
import type { PDFDocumentProxy } from 'pdfjs-dist'
import { supabase } from './supabase'
import { base } from './db'
import { televerserGros } from './televersement'
import type { DocumentChantier } from './types'
import './plans' // configure le moteur PDF

/**
 * Découpage des plans page par page.
 *
 * À l'import (sur l'ordinateur d'un administrateur), chaque page du PDF est
 * enregistrée dans son propre petit fichier PDF. Sur le chantier, le
 * téléphone ne télécharge que les pages réellement ouvertes : une dizaine de
 * pages de 300 à 800 Ko au lieu d'un fichier de 150 Mo.
 *
 * Le PDF d'origine est conservé tel quel : il sert à la lecture des
 * cartouches et au téléchargement du jeu complet.
 *
 * Chemin des pages : <chantier>/pages/<document>/<numéro>.pdf
 * (le premier segment est le chantier : les permissions du stockage
 * s'appliquent donc aux pages exactement comme au document d'origine).
 */

export function cheminPage(doc: Pick<DocumentChantier, 'id' | 'chantier_id'>, n: number): string {
  return `${doc.chantier_id}/pages/${doc.id}/${String(n).padStart(4, '0')}.pdf`
}

/** Découpe le PDF et envoie chaque page. Reprend là où il s'était arrêté. */
export async function decouperEtTeleverser(
  doc: Pick<DocumentChantier, 'id' | 'chantier_id'>,
  octets: ArrayBuffer,
  progression: (fait: number, total: number) => void,
  annule: () => boolean
): Promise<number> {
  const { PDFDocument } = await import('pdf-lib')
  const source = await PDFDocument.load(octets, { ignoreEncryption: true, updateMetadata: false })
  const total = source.getPageCount()

  // Pages déjà envoyées (un découpage interrompu reprend sans tout refaire).
  const dejaLa = new Set<string>()
  const { data: liste } = await supabase.storage
    .from('documents')
    .list(`${doc.chantier_id}/pages/${doc.id}`, { limit: 10000 })
  for (const f of liste ?? []) dejaLa.add(f.name)

  let fait = 0
  const suivante = { n: 0 }
  // Trois envois en parallèle : bien plus rapide, sans saturer la connexion.
  async function ouvrier() {
    for (;;) {
      if (annule()) return
      const i = suivante.n++
      if (i >= total) return
      const nom = `${String(i + 1).padStart(4, '0')}.pdf`
      if (!dejaLa.has(nom)) {
        const une = await PDFDocument.create()
        const [copie] = await une.copyPages(source, [i])
        une.addPage(copie)
        const bytes = await une.save({ useObjectStreams: true })
        const fichier = new File([new Uint8Array(bytes) as BlobPart], nom, { type: 'application/pdf' })
        const chemin = cheminPage(doc, i + 1)
        if (fichier.size > 6 * 1024 * 1024) {
          await televerserGros('documents', chemin, fichier, () => {})
        } else {
          const { error } = await supabase.storage
            .from('documents')
            .upload(chemin, fichier, { contentType: 'application/pdf', upsert: true })
          if (error) throw error
        }
      }
      fait++
      progression(fait, total)
    }
  }
  await Promise.all([ouvrier(), ouvrier(), ouvrier()])
  if (annule()) throw new Error('Découpage interrompu.')

  const { error } = await supabase
    .from('documents')
    .update({ nb_pages: total, pages_decoupees: true })
    .eq('id', doc.id)
  if (error) throw error
  return total
}

/**
 * Ouvre une seule page d'un plan découpé. La page est gardée sur l'appareil
 * après la première ouverture : elle se rouvre ensuite sans réseau.
 */
export async function ouvrirPage(
  doc: Pick<DocumentChantier, 'id' | 'chantier_id'>,
  n: number
): Promise<PDFDocumentProxy> {
  const cle = cheminPage(doc, n)
  let octets = (await base.plans.get(cle))?.octets
  if (!octets) {
    if (!navigator.onLine) {
      throw new Error("Cette page n'a pas encore été ouverte sur cet appareil et il n'y a pas de réseau.")
    }
    const { data, error } = await supabase.storage.from('documents').download(cle)
    if (error || !data) throw error ?? new Error('Page introuvable.')
    octets = await data.arrayBuffer()
    try {
      await base.plans.put({ cle, octets, conserve_le: Date.now() })
    } catch {
      // appareil plein : on affiche quand même
    }
  }
  return pdfjs.getDocument({ data: new Uint8Array(octets.slice(0)) }).promise
}
